import { Position } from '@busybusy/webapp-react-ui';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { NotCertifiedIcon } from 'assets/icons';
import { IconButton } from 'components';
import DropDownPicker from 'components/foundation/pickers/DropDownPicker/DropDownPicker';
import SimplePickerRow from 'components/foundation/pickers/SimplePickerRow/SimplePickerRow';
import { DEFAULT_PICKER_PAGE_SIZE, findOrFetchPickerItem } from 'components/foundation/pickers/utils';
import { useDebounce, useOrganization } from 'hooks';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { useReactQueryLazyLoading } from 'hooks/react-query/useReactQueryLazyLoading/useReactQueryLazyLoading';
import { t } from 'i18next';
import * as React from 'react';
import { ReactNode, useRef, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { getSafeBoolean } from 'utils/optionalUtils';
import { getCostCodeDisplay } from 'utils/stringUtils';
import { CostCodePickerItem, useCostCodePickerData } from './hooks/useCostCodePickerData';

export interface ICostCodePickerProps {
  value: string | null;
  onSelect: (costCodeId: string | null) => void;
  considerProjectSpecificCostCodes?: boolean;
  projectId?: string | null;
  minWidth?: string;
  placeholder?: string;
  error?: boolean;
  position?: Position;
  searchArchived?: boolean;
  className?: ClassName;
  dropDownButtonRender?: ReactNode;
  closeButtonRender?: (handleClear: (event: React.KeyboardEvent | React.MouseEvent) => void) => ReactNode;
  showMissingCertsWarning?: boolean;
}

const CostCodePicker = (props: ICostCodePickerProps) => {
  const {
    value,
    onSelect,
    minWidth = '350px',
    error,
    placeholder,
    position,
    projectId,
    searchArchived,
    considerProjectSpecificCostCodes = true,
    closeButtonRender,
    dropDownButtonRender,
    className,
    showMissingCertsWarning,
  } = props;

  const organization = useOrganization();
  const baseQueryKey = useReactQueryBaseKey();
  const [searchValue, setSearchValue] = useState('');
  const searchDebounce = useDebounce(searchValue, 250);
  const [isOpen, setIsOpen] = useState(false);
  const scroller = useRef<HTMLElement>();

  const costCodeOptions = { projectId, considerProjectSpecificCostCodes, searchArchived };
  const { fetchPage, fetchSelected } = useCostCodePickerData(costCodeOptions);

  function onClose() {
    setIsOpen(false);
    setSearchValue('');
  }

  function handleSelect(model: CostCodePickerItem | null) {
    onSelect(model ? model.id : null);
  }

  const {
    data,
    isLoading,
    isError: isDataError,
    loadedAll,
  } = useReactQueryLazyLoading(
    scroller.current,
    [...baseQueryKey, 'cost-code-picker', searchDebounce, costCodeOptions],
    (after, pageSize) => fetchPage(after, pageSize, searchDebounce),
    DEFAULT_PICKER_PAGE_SIZE,
    { enabled: isOpen }
  );

  const { data: selectedCostCode } = useQuery({
    queryKey: [...baseQueryKey, 'cost-code-picker-by-id', value],
    queryFn: () => findOrFetchPickerItem(value!, data, fetchSelected),
    initialData: null,
    enabled: !!value,
  });

  function renderValueTemplate() {
    if (selectedCostCode) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div className="ellipsis">{getCostCodeDisplay(selectedCostCode)}</div>
          {showMissingCertsWarning && (
            <IconButton
              svg={NotCertifiedIcon}
              tooltipLabel={t(
                'The required certification to clock time to this cost code is either missing or expired for one or more selected employees.'
              )}
            />
          )}
        </div>
      );
    }
    return <></>;
  }

  function renderRow(costCode: CostCodePickerItem) {
    return <SimplePickerRow value={getCostCodeDisplay(costCode)} searchValue={searchValue} />;
  }

  const classes = classNames('cost-code-picker', className);

  if (!organization.trackCostCode) {
    return null;
  }

  return (
    <DropDownPicker
      value={value}
      dataError={isDataError}
      error={error}
      data={data ?? []}
      loadedAll={loadedAll}
      onSelect={handleSelect}
      onClose={onClose}
      onOpen={() => setIsOpen(true)}
      onSearch={setSearchValue}
      searchValue={searchValue}
      valueTemplate={renderValueTemplate()}
      renderRow={renderRow}
      minWidth={minWidth}
      position={position}
      placeholder={placeholder}
      className={classes}
      closeButtonRender={closeButtonRender}
      dropDownButtonRender={dropDownButtonRender}
      setScrollerRef={(scrollerRef) => (scroller.current = scrollerRef)}
      isLoading={isLoading}
      disabled={
        !projectId &&
        getSafeBoolean(organization.useProjectCostCodeScoping) &&
        getSafeBoolean(considerProjectSpecificCostCodes)
      }
    />
  );
};

export default CostCodePicker;
